import {
    Box, Sheet, Stack, Typography, IconButton,
    Input, Tooltip, Divider, Select, Option, Button,
} from "@mui/joy";
import { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

// ─── Icons ────────────────────────────────────────────────────────────────────
import FolderRoundedIcon              from "@mui/icons-material/FolderRounded";
import FolderOpenRoundedIcon          from "@mui/icons-material/FolderOpenRounded";
import DescriptionRoundedIcon         from "@mui/icons-material/DescriptionRounded";
import ChevronRightRoundedIcon        from "@mui/icons-material/ChevronRightRounded";
import DeleteOutlineRoundedIcon       from "@mui/icons-material/DeleteOutlineRounded";
import CreateNewFolderRoundedIcon     from "@mui/icons-material/CreateNewFolderRounded";
import NoteAddRoundedIcon             from "@mui/icons-material/NoteAddRounded";
import FormatBoldRoundedIcon          from "@mui/icons-material/FormatBoldRounded";
import FormatItalicRoundedIcon        from "@mui/icons-material/FormatItalicRounded";
import FormatUnderlinedRoundedIcon    from "@mui/icons-material/FormatUnderlinedRounded";
import FormatStrikethroughRoundedIcon from "@mui/icons-material/FormatStrikethroughRounded";
import FormatQuoteRoundedIcon         from "@mui/icons-material/FormatQuoteRounded";
import FormatListBulletedRoundedIcon  from "@mui/icons-material/FormatListBulletedRounded";
import FormatListNumberedRoundedIcon  from "@mui/icons-material/FormatListNumberedRounded";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CodeRoundedIcon                from "@mui/icons-material/CodeRounded";
import HorizontalRuleRoundedIcon      from "@mui/icons-material/HorizontalRuleRounded";
import ChevronLeftRoundedIcon         from "@mui/icons-material/ChevronLeftRounded";
import EditNoteRoundedIcon            from "@mui/icons-material/EditNoteRounded";
import SaveRoundedIcon                from "@mui/icons-material/SaveRounded";
import CheckRoundedIcon               from "@mui/icons-material/CheckRounded";

// ─── Initial data ─────────────────────────────────────────────────────────────
let _nextId = 10;
const genId = () => `node-${++_nextId}`;

const INIT_TREE = [
    {
        id: "f1", type: "folder", name: "Рабочие заметки", open: true,
        children: [
            {
                id: "f1-1", type: "folder", name: "Проект CRM", open: false,
                children: [
                    { id: "n1", type: "note", name: "Архитектура API", content: "<h2>Архитектура API</h2><p>Основные эндпоинты для CRM-системы:</p><ul><li>/api/deals — список сделок</li><li>/api/auth — авторизация</li><li>/api/users — пользователи</li></ul><blockquote>Важно: все запросы требуют JWT-токен в заголовке Authorization</blockquote>" },
                    { id: "n2", type: "note", name: "Встреча 10 марта", content: "<h2>Встреча с командой — 10 марта</h2><p>Обсудили:</p><ol><li>Дедлайны по модулю оплат</li><li>Интеграция с Битрикс</li><li>Ревью кода Алексея</li></ol>" },
                ]
            },
            { id: "n3", type: "note", name: "Идеи для дашборда", content: "<h2>Идеи для дашборда</h2><p>Хочу добавить виджет активности, быстрые фильтры и интерактивную воронку сделок.</p>" },
        ]
    },
    {
        id: "f2", type: "folder", name: "Личные заметки", open: false,
        children: [
            { id: "n4", type: "note", name: "Книги для чтения", content: "<h2>Список книг</h2><p>Clean Code — Robert Martin</p><p>The Pragmatic Programmer</p><p>Domain-Driven Design</p>" },
        ]
    },
];

const FONT_OPTIONS = [
    { value: "inherit",                      label: "По умолчанию" },
    { value: "'Inter', sans-serif",          label: "Inter" },
    { value: "Georgia, serif",               label: "Georgia" },
    { value: "'Merriweather', serif",        label: "Merriweather" },
    { value: "'JetBrains Mono', monospace",  label: "JetBrains Mono" },
    { value: "'Courier New', monospace",     label: "Courier New" },
];

// ─── Tree helpers ─────────────────────────────────────────────────────────────
function findNode(nodes, id) {
    for (const n of nodes) {
        if (n.id === id) return n;
        if (n.children) { const f = findNode(n.children, id); if (f) return f; }
    }
    return null;
}
function updateNode(nodes, id, updater) {
    return nodes.map(n => {
        if (n.id === id) return updater(n);
        if (n.children) return { ...n, children: updateNode(n.children, id, updater) };
        return n;
    });
}
function deleteNode(nodes, id) {
    return nodes.filter(n => n.id !== id)
        .map(n => n.children ? { ...n, children: deleteNode(n.children, id) } : n);
}
function addToNode(nodes, parentId, newNode) {
    if (parentId === "root") return [...nodes, newNode];
    return nodes.map(n => {
        if (n.id === parentId && n.type === "folder")
            return { ...n, open: true, children: [...(n.children || []), newNode] };
        if (n.children) return { ...n, children: addToNode(n.children, parentId, newNode) };
        return n;
    });
}

// ─── Format helpers ───────────────────────────────────────────────────────────
// Toggle block: если уже в нужном теге — снять (→ p), иначе применить
function toggleBlock(tag) {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const node = range.commonAncestorContainer;
    const el = node.nodeType === 3 ? node.parentElement : node;
    const existing = el.closest(tag);

    if (existing) {
        const p = document.createElement("p");
        p.innerHTML = existing.innerHTML;
        
        // Вставляем p строго на место existing в DOM
        const parent = existing.parentNode;
        parent.insertBefore(p, existing);
        parent.removeChild(existing);
        
        // Восстанавливаем курсор
        const newRange = document.createRange();
        newRange.selectNodeContents(p);
        newRange.collapse(false);
        sel.removeAllRanges();
        sel.addRange(newRange);
        return; // ← добавь return чтобы не упасть в ветку else
    } else {
        // Ищем ближайший блочный элемент — но НЕ сам редактор ([contenteditable])
        const blockEl = el.closest("p, h1, h2, h3, h4, li, blockquote, pre")
              || (el.isContentEditable ? null : el.closest("[contenteditable]") && el);
        if (!blockEl) {
            // «голый» текст прямо в редакторе — сначала оборачиваем в p
            document.execCommand("formatBlock", false, "p");
            // После этого вызываем toggleBlock повторно (теперь blockEl найдётся)
            toggleBlock(tag);
            return;
        }
        const wrapper = document.createElement(tag);
        wrapper.innerHTML = blockEl.innerHTML;
        blockEl.replaceWith(wrapper);

        const newRange = document.createRange();
        newRange.selectNodeContents(wrapper);
        newRange.collapse(false);
        sel.removeAllRanges();
        sel.addRange(newRange);
    }
}

function isInlineActive(cmd) {
    try { return document.queryCommandState(cmd); } catch { return false; }
}
function isBlockActive(tag) {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return false;
    const node = sel.getRangeAt(0).commonAncestorContainer;
    const el = node.nodeType === 3 ? node.parentElement : node;
    return !!el.closest(tag);
}

function isListActive(listTag) {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return false;
    const node = sel.getRangeAt(0).commonAncestorContainer;
    const el = node.nodeType === 3 ? node.parentElement : node;
    return !!el.closest(listTag);
}

// ─── FloatingToolbar ─────────────────────────────────────────────────────────
function FloatingToolbar({ scrollAreaRef }) {
    const [visible, setVisible] = useState(false);
    const [pos, setPos]         = useState({ top: 0, left: 0 });
    const [tick, setTick]       = useState(0);

    useEffect(() => {
        const onSel = () => {
            const sel = window.getSelection();
            if (!sel || sel.isCollapsed || !scrollAreaRef.current) {
                setVisible(false);
                return;
            }
            // Проверяем что выделение внутри нашей зоны редактирования
            const editorEl = scrollAreaRef.current.querySelector("[contenteditable]");
            if (!editorEl || !editorEl.contains(sel.anchorNode)) {
                setVisible(false);
                return;
            }

            const range    = sel.getRangeAt(0);
            const selRect  = range.getBoundingClientRect();
            const areaRect = scrollAreaRef.current.getBoundingClientRect();
            const scrollTop = scrollAreaRef.current.scrollTop;

            const TOOLBAR_H = 56;
            const TOOLBAR_W = 360;

            // Позиция относительно scrollArea (с учётом скролла)
            let top  = (selRect.top - areaRect.top) + scrollTop - TOOLBAR_H - 12;
            let left = (selRect.left - areaRect.left) + selRect.width / 2 - TOOLBAR_W / 2;

            left = Math.max(8, Math.min(left, areaRect.width - TOOLBAR_W - 8));
            if (top < scrollTop + 4) {
                top = (selRect.top - areaRect.top) + scrollTop + selRect.height + 10;
            }

            setPos({ top, left });
            setVisible(true);
            setTick(v => v + 1);
        };

        document.addEventListener("selectionchange", onSel);
        return () => document.removeEventListener("selectionchange", onSel);
    }, [scrollAreaRef]);

    const execInline = (cmd) => { document.execCommand(cmd); setTick(v => v + 1); };
    const execBlock  = (tag) => { toggleBlock(tag);           setTick(v => v + 1); };

    const TOOLS = [
        { icon: <FormatBoldRoundedIcon />,              action: () => execInline("bold"),               isActive: () => isInlineActive("bold"),          tip: "Жирный" },
        { icon: <FormatItalicRoundedIcon />,            action: () => execInline("italic"),             isActive: () => isInlineActive("italic"),        tip: "Курсив" },
        { icon: <FormatUnderlinedRoundedIcon />,        action: () => execInline("underline"),          isActive: () => isInlineActive("underline"),     tip: "Подчёркнутый" },
        { icon: <FormatStrikethroughRoundedIcon />,     action: () => execInline("strikeThrough"),      isActive: () => isInlineActive("strikeThrough"), tip: "Зачёркнутый" },
        { sep: true },
        { icon: <FormatQuoteRoundedIcon />,             action: () => execBlock("blockquote"),          isActive: () => isBlockActive("blockquote"),     tip: "Цитата" },
        { icon: <CodeRoundedIcon />,                    action: () => execBlock("pre"),                 isActive: () => isBlockActive("pre"),            tip: "Код" },
        { icon: <FormatListBulletedRoundedIcon />,      action: () => execInline("insertUnorderedList"),isActive: () => isInlineActive("insertUnorderedList"), tip: "Список" },
    ];

    if (!visible) return null;

    return (
        <AnimatePresence>
            <motion.div
                key="ftb"
                initial={{ opacity: 0, y: 8, scale: 0.93 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.93 }}
                transition={{ duration: 0.14, ease: [0.4, 0, 0.2, 1] }}
                onMouseDown={e => e.preventDefault()}
                style={{
                    position: "absolute",
                    top:  pos.top,
                    left: pos.left,
                    zIndex: 300,
                    backgroundColor: "rgba(255, 255, 255, 0.35)",
                    backdropFilter: "blur(3px)",
                    // border: "0.5px solid var(--joy-palette-divider)",
                    borderRadius: 16,
                    // filter: 'blur(2px)',
                    boxShadow: "0 12px 40px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.07)",
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 12px",
                    gap: 4,
                }}
            >
                {TOOLS.map((t, i) => {
                    if (t.sep) return (
                        <Divider key={i}  />
                    );
                    const active = t.isActive?.();
                    return (
                        <Tooltip key={i} title={t.tip} size="sm" placement="top">
                            <IconButton
                                size="xs"
                                variant={active ? "soft" : "plain"}
                                color={active ? "primary" : "neutral"}
                                onMouseDown={e => { e.preventDefault(); t.action(); }}
                                sx={{
                                    borderRadius: 10, width: 33, height: 33, minWidth: 0,
                                    "& svg": { fontSize: 20 },
                                    transition: "all 0.12s",
                                }}
                            >
                                {t.icon}
                            </IconButton>
                        </Tooltip>
                    );
                })}
            </motion.div>
        </AnimatePresence>
    );
}

// ─── TopToolbar ───────────────────────────────────────────────────────────────
function TopToolbar({ onInsert, font, onFontChange, onSave, saved }) {
    const [tick, setTick] = useState(0);

    useEffect(() => {
        const h = () => setTick(v => v + 1);
        document.addEventListener("selectionchange", h);
        return () => document.removeEventListener("selectionchange", h);
    }, []);

    const execInline = (cmd) => { document.execCommand(cmd); setTick(v => v + 1); };
    const execBlock  = (tag) => { toggleBlock(tag);           setTick(v => v + 1); };

    const GROUPS = [
        // Заголовки
        [
            { label: "H1", action: () => execBlock("h1"), isActive: () => isBlockActive("h1"), tip: "Заголовок 1", lsx: { fontSize: "0.85rem", fontWeight: 800 } },
            { label: "H2", action: () => execBlock("h2"), isActive: () => isBlockActive("h2"), tip: "Заголовок 2", lsx: { fontSize: "0.8rem",  fontWeight: 800 } },
            { label: "H3", action: () => execBlock("h3"), isActive: () => isBlockActive("h3"), tip: "Заголовок 3", lsx: { fontSize: "0.73rem", fontWeight: 800 } },
        ],
        // Inline-форматирование
        [
            { icon: <FormatBoldRoundedIcon />,              action: () => execInline("bold"),              isActive: () => isInlineActive("bold"),          tip: "Жирный (Ctrl+B)" },
            { icon: <FormatItalicRoundedIcon />,            action: () => execInline("italic"),            isActive: () => isInlineActive("italic"),        tip: "Курсив (Ctrl+I)" },
            { icon: <FormatUnderlinedRoundedIcon />,        action: () => execInline("underline"),         isActive: () => isInlineActive("underline"),     tip: "Подчёркнутый (Ctrl+U)" },
            { icon: <FormatStrikethroughRoundedIcon />,     action: () => execInline("strikeThrough"),     isActive: () => isInlineActive("strikeThrough"), tip: "Зачёркнутый" },
        ],
        // Списки и чекбокс
        [
            { icon: <FormatListBulletedRoundedIcon />,      action: () => execInline("insertUnorderedList"), isActive: () => isListActive("ul"), tip: "Маркированный список" },
            { icon: <FormatListNumberedRoundedIcon />,      action: () => execInline("insertOrderedList"),   isActive: () => isListActive("ol"),   tip: "Нумерованный список" },
            { icon: <CheckBoxOutlineBlankRoundedIcon />,    action: () => onInsert("checkbox"),              isActive: () => false,                                 tip: "Вставить чекбокс" },
        ],
        // Блоки
        [
            { icon: <FormatQuoteRoundedIcon />,             action: () => execBlock("blockquote"),         isActive: () => isBlockActive("blockquote"),  tip: "Цитата (повтор — снять)" },
            { icon: <CodeRoundedIcon />,                    action: () => execBlock("pre"),                isActive: () => isBlockActive("pre"),         tip: "Блок кода (повтор — снять)" },
            { icon: <HorizontalRuleRoundedIcon />,          action: () => document.execCommand("insertHorizontalRule"), isActive: () => false,           tip: "Разделитель" },
        ],
    ];

    return (
        <Sheet sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",

            gap: 0.75,
            px: 1.2,
            py: 1,
            
            mb: 1,
            borderRadius: 20,
            // borderBottom: "1px solid",
            // borderColor: "divider",
            bgcolor: "neutral.100",
            // flexShrink: 0,
        }}>
            {/* Font selector */}
            <Select
                size="sm"
                value={font}
                onChange={(_, v) => onFontChange(v)}
                sx={{
                    minWidth: 155,
                    fontSize: "0.84rem",
                    borderRadius: "10px",
                    height: 36,
                    "--Select-focusedHighlight": "rgba(99,102,241,0.3)",
                }}
                slotProps={{ listbox: { sx: { zIndex: 500, borderRadius: "12px", fontSize: "0.84rem" } } }}
            >
                {FONT_OPTIONS.map(f => (
                    <Option key={f.value} value={f.value}
                        sx={{ fontSize: "0.84rem", fontFamily: f.value !== "inherit" ? f.value : undefined }}
                    >
                        {f.label}
                    </Option>
                ))}
            </Select>

            <Divider orientation="horizontal" />

            {/* Format groups */}
            {GROUPS.map((group, gi) => (
                <Stack key={gi} direction="row" alignItems="center" spacing={0.3}>
                    {gi > 0 && <Divider orientation="vertical" sx={{ mr: 1 }} />}
                    {group.map((t, i) => {
                        const active = t.isActive?.();
                        return (
                            <Tooltip key={i} title={t.tip} size="sm" placement="bottom">
                                <IconButton
                                    size="sm"
                                    variant={active ? "soft" : "plain"}
                                    color={active ? "primary" : "neutral"}
                                    onMouseDown={e => { e.preventDefault(); t.action(); }}
                                    sx={{
                                        borderRadius: 9,
                                        width: 36, height: 36,
                                        minWidth: 0,
                                        transition: "all 0.12s",
                                        "& svg": { fontSize: 20 },
                                    }}
                                >
                                    {t.icon || (
                                        <Typography sx={{ ...t.lsx, lineHeight: 1 }}>{t.label}</Typography>
                                    )}
                                </IconButton>
                            </Tooltip>
                        );
                    })}
                </Stack>
            ))}

            <Box sx={{ flex: 1 }} />

            {/* Save */}
            <Tooltip title="Сохранить (Ctrl+S)" size="sm" placement="bottom">
                <Button
                    size="sm"
                    variant={saved ? "soft" : "solid"}
                    color={saved ? "success" : "primary"}
                    startDecorator={saved
                        ? <CheckRoundedIcon sx={{ fontSize: 17 }} />
                        : <SaveRoundedIcon  sx={{ fontSize: 17 }} />
                    }
                    onMouseDown={e => e.preventDefault()}
                    onClick={onSave}
                    sx={{
                        borderRadius: 12,
                        fontWeight: 600, fontSize: "0.84rem",
                        height: 36, px: 2,
                        transition: "all 0.2s",
                        flexShrink: 0,
                        minWidth: 120,
                    }}
                >
                    {saved ? "Сохранено" : "Сохранить"}
                </Button>
            </Tooltip>
        </Sheet>
    );
}

// ─── TreeNode ─────────────────────────────────────────────────────────────────
function TreeNode({ node, depth = 0, activeId, onSelect, onAddFolder, onAddNote, onDelete, onToggle, onRename }) {
    const [hovered, setHovered] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editVal, setEditVal] = useState(node.name);
    const inputRef  = useRef(null);
    const isActive  = activeId === node.id;

    function submit() {
        if (editVal.trim()) onRename(node.id, editVal.trim());
        setEditing(false);
    }
    useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

    return (
        <Box>
            <Box
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                sx={{
                    display: "flex", alignItems: "center",
                    pl: `${10 + depth * 18}px`, pr: 1,
                    py: 0,
                    my: 0.2,
                    borderRadius: 10,
                    cursor: "pointer",
                    bgcolor: isActive ? "primary.softBg" : hovered ? "neutral.softBg" : "transparent",
                    transition: "background 0.12s",
                    gap: 0.75, minHeight: 36,
                }}
                onClick={() => node.type === "folder" ? onToggle(node.id) : onSelect(node.id)}
            >
                {/* Chevron */}
                {node.type === "folder" ? (
                    <motion.div animate={{ rotate: node.open ? 90 : 0 }} transition={{ duration: 0.15 }}
                        style={{ display: "flex", flexShrink: 0 }}>
                        <ChevronRightRoundedIcon sx={{ fontSize: 17, color: "text.tertiary" }} />
                    </motion.div>
                ) : (
                    <Box sx={{ width: 17, flexShrink: 0 }} />
                )}

                {/* Icon */}
                <Box sx={{ flexShrink: 0, display: "flex" }}>
                    {node.type === "folder"
                        ? node.open
                            ? <FolderOpenRoundedIcon sx={{ fontSize: 18, color: "#f59e0b" }} />
                            : <FolderRoundedIcon    sx={{ fontSize: 18, color: "#f59e0b" }} />
                        : <DescriptionRoundedIcon sx={{ fontSize: 17, color: isActive ? "primary.plainColor" : "text.tertiary" }} />
                    }
                </Box>

                {/* Label / input */}
                {editing ? (
                    <Input ref={inputRef} size="sm" value={editVal}
                        onChange={e => setEditVal(e.target.value)}
                        onBlur={submit}
                        onKeyDown={e => { if (e.key === "Enter") submit(); if (e.key === "Escape") setEditing(false); }}
                        sx={{ flex: 1, fontSize: "0.88rem", py: 0, px: 0.5, minHeight: 0, height: 28, "--Input-focusedHighlight": "transparent" }}
                        onClick={e => e.stopPropagation()}
                    />
                ) : (
                    <Typography
                        onDoubleClick={e => { e.stopPropagation(); setEditing(true); }}
                        sx={{
                            flex: 1, fontWeight: isActive ? 600 : 400,
                            color: isActive ? "primary.plainColor" : "text.primary",
                            fontSize: "0.88rem", lineHeight: 1.2, userSelect: "none",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}
                    >
                        {node.name}
                    </Typography>
                )}

                {/* Hover actions */}
                <AnimatePresence>
                    {hovered && !editing && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            style={{ display: "flex", gap: 2, flexShrink: 0 }}
                            onClick={e => e.stopPropagation()}
                        >
                            {node.type === "folder" && (
                                <>
                                    <Tooltip title="Новая папка" size="sm">
                                        <IconButton size="sm" variant="plain" color="neutral"
                                            sx={{ width: 26, height: 26, minWidth: 0, borderRadius: 7 }}
                                            onClick={() => onAddFolder(node.id)}
                                        ><CreateNewFolderRoundedIcon sx={{ fontSize: 15 }} /></IconButton>
                                    </Tooltip>
                                    <Tooltip title="Новая заметка" size="sm">
                                        <IconButton size="sm" variant="plain" color="neutral"
                                            sx={{ width: 26, height: 26, minWidth: 0, borderRadius: 7 }}
                                            onClick={() => onAddNote(node.id)}
                                        ><NoteAddRoundedIcon sx={{ fontSize: 15 }} /></IconButton>
                                    </Tooltip>
                                </>
                            )}
                            <Tooltip title="Удалить" size="sm">
                                <IconButton size="sm" variant="plain" color="danger"
                                    sx={{ width: 30, height: 20, minWidth: 0, borderRadius: 7 }}
                                    onClick={() => onDelete(node.id)}
                                ><DeleteOutlineRoundedIcon sx={{ fontSize: 15 }} /></IconButton>
                            </Tooltip>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Box>

            {/* Children */}
            {node.type === "folder" && (
                <AnimatePresence initial={false}>
                    {node.open && (
                        <motion.div key="ch"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                            style={{ overflow: "hidden" }}
                        >
                            {(node.children || []).map(child => (
                                <TreeNode key={child.id} node={child} depth={depth + 1}
                                    activeId={activeId} onSelect={onSelect}
                                    onAddFolder={onAddFolder} onAddNote={onAddNote}
                                    onDelete={onDelete} onToggle={onToggle} onRename={onRename}
                                />
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>
            )}
        </Box>
    );
}

// ─── NotesSidebar ─────────────────────────────────────────────────────────────
function NotesSidebar({ tree, activeId, onSelect, onChange, collapsed }) {
    const toggle     = (id)       => onChange(p => updateNode(p, id, n => ({ ...n, open: !n.open })));
    const del        = (id)       => onChange(p => deleteNode(p, id));
    const rename     = (id, name) => onChange(p => updateNode(p, id, n => ({ ...n, name })));
    const addFolder  = (parentId) => {
        const node = { id: genId(), type: "folder", name: "Новая папка", open: false, children: [] };
        onChange(p => addToNode(p, parentId, node));
    };
    const addNote    = (parentId) => {
        const node = { id: genId(), type: "note", name: "Новая заметка", content: "<p></p>" };
        onChange(p => addToNode(p, parentId, node));
        setTimeout(() => onSelect(node.id), 50);
    };

    return (
        <Sheet variant="soft" sx={{
            mr: 2,
            p: 1,
            borderRadius: 20,
            width: collapsed ? 0 : 256,
            minWidth: collapsed ? 0 : 256,
            overflow: collapsed ? "hidden" : "hidden auto",
            height: "-webkit-fill-available",
            display: "flex", flexDirection: "column",
            transition: "width 0.25s cubic-bezier(0.4,0,0.2,1), min-width 0.25s",
            bgcolor: "neutral.50", 
            flexShrink: 0,
        }}>
            <Box sx={{ backgroundColor: 'neutral.100', borderRadius: 15, px: 2, pt: 1, pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
                <Typography level="title-lg" sx={{ fontWeight: 700, color: "text.tertiary" }}>
                    Папки
                </Typography>
                <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Новая папка" size="sm">
                        <IconButton size="sm" variant="plain" color="neutral"
                            sx={{ width: 30, height: 30, minWidth: 0, borderRadius: 8 }}
                            onClick={() => addFolder("root")}
                        ><CreateNewFolderRoundedIcon sx={{ fontSize: 17 }} /></IconButton>
                    </Tooltip>
                    <Tooltip title="Новая заметка" size="sm">
                        <IconButton size="sm" variant="plain" color="neutral"
                            sx={{ width: 30, height: 30, minWidth: 0, borderRadius: 8 }}
                            onClick={() => addNote("root")}
                        ><NoteAddRoundedIcon sx={{ fontSize: 17 }} /></IconButton>
                    </Tooltip>
                </Stack>
            </Box>

            {/* <Divider /> */}

            <Box sx={{ flex: 1, overflow: "auto", py: 1, px: 0.75 }}>
                {tree.map(node => (
                    <TreeNode key={node.id} node={node}
                        activeId={activeId} onSelect={onSelect}
                        onAddFolder={addFolder} onAddNote={addNote}
                        onDelete={del} onToggle={toggle} onRename={rename}
                    />
                ))}
            </Box>
        </Sheet>
    );
}

// ─── NoteEditor ───────────────────────────────────────────────────────────────
function NoteEditor({ note, onContentChange, onNameChange, onSave }) {
    const editorRef     = useRef(null);
    const scrollAreaRef = useRef(null);
    const saveTimer     = useRef(null);
    const [titleEditing, setTitleEditing] = useState(false);
    const [font, setFont]   = useState("inherit");
    const [saved, setSaved] = useState(false);

    // Sync html when note switches
    useEffect(() => {
        if (editorRef.current && note) {
            editorRef.current.innerHTML = note.content || "<p></p>";
        }
        setSaved(false);
    }, [note?.id]);

    const doSave = useCallback(() => {
        setSaved(true);
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => setSaved(false), 2200);
    }, []);

    // Ctrl+S
    useEffect(() => {
        const h = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "s") { e.preventDefault(); doSave(); }
        };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    });

    const handleKeyDown = useCallback((e) => {

        // Tab внутри списка — indent / outdent
        if (e.key === 'Tab') {
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0) {
                const node = sel.getRangeAt(0).commonAncestorContainer;
                const el = node.nodeType === 3 ? node.parentElement : node;
                const li = el.closest('li');
                if (li) {
                    e.preventDefault();
                    if (e.shiftKey) {
                        document.execCommand('outdent');
                    } else {
                        document.execCommand('indent');
                    }
                    return;
                }
            }
        }

        // Ctrl+Shift+Z — redo
        if (e.ctrlKey && e.shiftKey && (e.key === 'z' || e.key === 'Z')) {
            e.preventDefault();
            document.execCommand('redo');
            return;
        }

        // Ctrl+S
        if ((e.ctrlKey || e.metaKey) && e.key === 's') {
            e.preventDefault();
            doSave();
            return;
        }

        // Ctrl+B / I / U
        if (e.ctrlKey && e.key === 'b') { e.preventDefault(); document.execCommand('bold');      return; }
        if (e.ctrlKey && e.key === 'i') { e.preventDefault(); document.execCommand('italic');    return; }
        if (e.ctrlKey && e.key === 'u') { e.preventDefault(); document.execCommand('underline'); return; }

    }, [doSave]);

    const handleInsert = useCallback((type) => {
        if (type === "checkbox") {
            document.execCommand("insertHTML", false,
                `<p><label style="display:inline-flex;align-items:center;gap:8px;cursor:pointer;user-select:none"><input type="checkbox" style="width:15px;height:15px;cursor:pointer;accent-color:#6366f1"> <span>Задача</span></label></p>`
            );
        }
    }, []);

    const handleInput = useCallback(() => {
        if (editorRef.current && note) {
            onContentChange(note.id, editorRef.current.innerHTML);
            setSaved(false);
        }
    }, [note, onContentChange]);

    if (!note) {
        return (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} style={{ textAlign: "center" }}>
                    <EditNoteRoundedIcon sx={{ fontSize: 60, opacity: 0.13, mb: 2 }} />
                    <Typography level="body-lg" sx={{ color: "text.tertiary", fontWeight: 500 }}>
                        Выберите заметку
                    </Typography>
                    <Typography level="body-sm" sx={{ color: "text.tertiary", mt: 0.5 }}>
                        или создайте новую в боковой панели
                    </Typography>
                </motion.div>
            </Box>
        );
    }

    return (
        <Box
            component={motion.div}
            key={note.id}
            initial={{ opacity: 0, x: 15 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }}
            exit={{ opacity: 0, y: 15 }}
            sx={{ 
                flex: 1, display: "flex", flexDirection: "column", overflow: "hidden",
                borderRadius: 20
            }}
        >
            <TopToolbar
                onInsert={handleInsert}
                font={font}
                onFontChange={setFont}
                onSave={doSave}
                saved={saved}
            />

            {/* Scrollable area — FloatingToolbar живёт здесь */}
            <Box ref={scrollAreaRef} sx={{ flex: 1, overflow: "auto", position: "relative" }}>

                <FloatingToolbar scrollAreaRef={scrollAreaRef} />

                {/* Title */}
                <Box sx={{ px: { xs: 4, md: 8 }, pt: 4, pb: 0 }}>
                    {titleEditing ? (
                        <Input
                            autoFocus
                            value={note.name}
                            onChange={e => onNameChange(note.id, e.target.value)}
                            onBlur={() => setTitleEditing(false)}
                            onKeyDown={e => { if (e.key === "Enter") setTitleEditing(false); }}
                            sx={{
                                fontSize: "1.9rem", fontWeight: 800, border: "none",
                                boxShadow: "none", bgcolor: "transparent", p: 0,
                                "--Input-focusedHighlight": "transparent",
                                borderBottom: "2px solid var(--joy-palette-primary-200)",
                                borderRadius: 0, mb: 3,
                                fontFamily: font !== "inherit" ? font : undefined,
                            }}
                        />
                    ) : (
                        <Typography
                            onClick={() => setTitleEditing(true)}
                            sx={{
                                fontSize: "clamp(1.5rem, 3vw, 1.9rem)",
                                fontWeight: 800, lineHeight: 1.1,
                                letterSpacing: "-0.5px", color: "text.primary",
                                mb: 3, cursor: "text",
                                fontFamily: font !== "inherit" ? font : undefined,
                                "&:hover": { color: "primary.plainColor" },
                                transition: "color 0.15s",
                            }}
                        >
                            {note.name}
                        </Typography>
                    )}
                </Box>

                {/* ContentEditable */}
                <Box
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={handleInput}
                    onKeyDown={handleKeyDown}
                    sx={{
                        px: { xs: 4, md: 8 },
                        pb: 10,
                        minHeight: "60vh",
                        outline: "none",
                        fontSize: "1rem",
                        lineHeight: 1.85,
                        color: "text.primary",
                        fontFamily: font,

                        "& h1": { fontSize: "1.75rem", fontWeight: 800, mt: 3, mb: 1, letterSpacing: "-0.4px", lineHeight: 1.2 },
                        "& h2": { fontSize: "1.35rem", fontWeight: 700, mt: 2.5, mb: 0.75, letterSpacing: "-0.3px" },
                        "& h3": { fontSize: "1.12rem", fontWeight: 700, mt: 2, mb: 0.5 },
                        "& p":  { my: 0.3 },
                        "& ul, & ol": { pl: "1.8em", my: 0.5 },
                        "& li": { mb: 0.3, pl: "0.2em" },
                        "& blockquote": {
                            borderLeft: "3px solid var(--joy-palette-primary-300)",
                            pl: 2.5, my: 2,
                            color: "text.secondary",
                            fontStyle: "italic",
                            bgcolor: "rgba(99,102,241,0.04)",
                            borderRadius: "0 10px 10px 0",
                            py: 0.75,
                        },
                        "& pre": {
                            bgcolor: "neutral.softBg",
                            borderRadius: "12px",
                            p: 2, my: 2,
                            fontSize: "0.88rem",
                            fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
                            overflow: "auto",
                            border: "1px solid",
                            borderColor: "divider",
                            whiteSpace: "pre-wrap",
                        },
                        "& code": {
                            bgcolor: "neutral.softBg",
                            px: 0.6, borderRadius: "5px",
                            fontSize: "0.85em",
                            fontFamily: "'JetBrains Mono', monospace",
                            border: "1px solid",
                            borderColor: "divider",
                        },
                        "& hr": { border: "none", borderTop: "1px solid var(--joy-palette-divider)", my: 3 },
                        "& input[type=checkbox]": { cursor: "pointer", accentColor: "#6366f1" },
                        "& a": { color: "primary.plainColor", textDecoration: "underline" },
                    }}
                />
            </Box>
        </Box>
    );
}

// ─── NotesPage ────────────────────────────────────────────────────────────────
export default function NotesPage() {
    const [tree, setTree]         = useState(INIT_TREE);
    const [activeId, setActiveId] = useState("n1");
    const [collapsed, setCollapsed] = useState(false);

    const activeNote = activeId ? findNode(tree, activeId) : null;
    const note       = activeNote?.type === "note" ? activeNote : null;

    const handleContentChange = useCallback((id, content) => {
        setTree(p => updateNode(p, id, n => ({ ...n, content })));
    }, []);
    const handleNameChange = useCallback((id, name) => {
        setTree(p => updateNode(p, id, n => ({ ...n, name })));
    }, []);
    const handleSave = useCallback((id, content) => {
        // Здесь будет запрос на Django backend
        // fetch('/api/notes/' + id + '/', { method: 'PATCH', body: JSON.stringify({ content }) })
        console.log('Saving note:', id);
    }, []);

    return (
        <Box sx={{ display: "flex", height: "calc(120dvh)", overflow: "hidden" }}>
            <NotesSidebar 
                tree={tree} 
                activeId={activeId} 
                onSelect={setActiveId}
                onChange={setTree} 
                collapsed={collapsed}
            />

            {/* Collapse toggle */}
            {/* <Box sx={{ position: "relative", flexShrink: 0 }}>
                <Box onClick={() => setCollapsed(v => !v)}
                    sx={{
                        position: "absolute", top: "50%", left: -1,
                        transform: "translateY(-50%)", zIndex: 10,
                        width: 18, height: 44,
                        borderRadius: "0 8px 8px 0",
                        bgcolor: "background.surface",
                        border: "1px solid", borderLeft: "none", borderColor: "divider",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        cursor: "pointer", transition: "background 0.15s",
                        "&:hover": { bgcolor: "neutral.softBg" },
                    }}
                >
                    {collapsed
                        ? <ChevronRightRoundedIcon sx={{ fontSize: 14, color: "text.tertiary" }} />
                        : <ChevronLeftRoundedIcon  sx={{ fontSize: 14, color: "text.tertiary" }} />
                    }
                </Box>
            </Box> */}

            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", bgcolor: "background.body" }}>
                <AnimatePresence mode="wait">
                    <NoteEditor
                        key={note?.id || "empty"}
                        note={note}
                        onContentChange={handleContentChange}
                        onNameChange={handleNameChange}
                        onSave={handleSave}
                    />
                </AnimatePresence>
            </Box>
        </Box>
    );
}